import PortalDialog, {PortalDialogProperties, PortalDialogState} from "../PortalDialog";
import React, {ReactNode} from "react";
import {Dialog} from "primereact/dialog";
import PortalDialogType from "../../PortalDialogType";
import MimeTypes from "../../../document/mimetype/MimeTypes";
import {WebServiceType, WebServiceTypes} from "@softvision/webpdf-wsclient-9-typescript";
import MimeTypeFilter from "../../../document/mimetype/MimeTypeFilter";
import Parameter from "../../parameter/Parameter";
import PortalDocument from "../../../document/PortalDocument";
import {Button} from "primereact/button";
import PortalDocumentManager from "../../../document/PortalDocumentManager";
import DialogDefault from "../../parameter/PortalDialogDefault";
import {InputText} from "primereact/inputtext";
import {InputTextarea} from "primereact/inputtextarea";
import {ColorPicker} from "primereact/colorpicker";
import {InputNumber} from "primereact/inputnumber";
import validator from "validator";
import MimeType from "../../../document/mimetype/MimeType";
import Message from "../../../../shared/log/Message";
import {MessageType} from "../../../../shared/log/MessageInterface";
import {
	AdminWebserviceStatus,
	BaseToolbox,
	Functions,
	Information,
	MarkupAnnotation,
	Security,
	Settings,
	SettingsInterface,
	SignatureEditProfile,
	ToolboxAnnotationAnnotation
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import PortalDialogSecurity, {Intention} from "../../PortalDialogSecurity";
import Translate from "../../../../shared/translation/Translate";
import Helper from "../../../../shared/components/Helper";
import AnnotationStrikeDialogFields from "../../field/annotation/AnnotationStrikeDialogFields";
import Icon from "../../../../shared/components/Icon";
import FunctionHelper from "../../../../shared/helper/FunctionHelper";

export class AnnotationStrikeEditDialogDefinition extends PortalDialog<ToolboxAnnotationAnnotation,
	PortalDialogProperties<ToolboxAnnotationAnnotation>, PortalDialogState<ToolboxAnnotationAnnotation>> {
	constructor(props: any) {
		super(props);

		this.state = {
			...this.state,
			fieldErrors: new Map<AnnotationStrikeDialogFields, string>()
		};
	}

	public static isEnabled(portalInfo: Information): boolean {
		return FunctionHelper.applyValue(
			portalInfo.webserviceStatus?.webservices?.toolbox?.enabled, AdminWebserviceStatus.getEnabledDefault()
		) && FunctionHelper.applyValue(
			portalInfo.functionsStatus?.editAnnotation, Functions.getEditAnnotationDefault()
		);
	}

	public static isActive(
		selectedDocuments: Array<PortalDocument>, portalInfo: Information, signatureEditProfile: SignatureEditProfile
	): boolean {
		let mimeTypes: Array<MimeType> = [];

		for (let selectedDocument of selectedDocuments) {
			mimeTypes.push(selectedDocument.getMimeType());

			if (selectedDocument.isLocked()) {
				return false;
			}

			if (!PortalDialogSecurity.checkRestrictions(
				PortalDialogType.ANNOTATION_STRIKE_EDIT, signatureEditProfile, Intention.editingAnnotations,
				selectedDocument
			)) {
				return false;
			}

			if (!selectedDocument.isEditable()) {
				return false;
			}
		}

		return this.isEnabled(portalInfo) && selectedDocuments.length === 1 &&
			MimeTypeFilter.checkSelection(mimeTypes, [MimeTypes.PDF]);
	}

	public renderDialog(): ReactNode {
		let classList: Array<string> = ["annotation-strike-edit-dialog"];
		if (this.state.submitted) {
			classList.push("submitted");
		}

		return (
			<Dialog className={classList.join(" ")} visible={this.props.visible} onHide={this.hide}
					header={this.renderHeader()} footer={this.renderFooter()}
					appendTo={document.getElementById("root")} resizable={false}>
				<div className="p-grid">
					{this.renderError()}
					<Helper {...this.props}>
						<Translate {...this.props} data={"dialog.annotation_strike.edit"} namespace={"help"}
								   markdown={true}/>
					</Helper>
					{this.renderContents()}
				</div>
			</Dialog>
		);
	}

	public renderContents(): ReactNode {
		let parameter: ToolboxAnnotationAnnotation | undefined = this.state.parameter?.data;
		let errors: Map<AnnotationStrikeDialogFields, string> = this.state.fieldErrors;

		if (!FunctionHelper.exists(parameter?.edit?.markup)) {
			return null;
		}

		let annotationParameter: MarkupAnnotation = parameter!.edit!.markup!;

		return (
			<>
				<fieldset className="p-col-12 p-grid">
					<div className="p-col-12 p-grid"
						 hidden={!this.state.fields?.contains(AnnotationStrikeDialogFields.CREATOR)}>
						<label className="p-col-12 p-sm-4" htmlFor="control-creator">
							<Translate {...this.props} data="annotation.properties.creator.label" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-8 p-grid">
							<InputText id="control-creator"
									   className={errors.has(AnnotationStrikeDialogFields.CREATOR) ? "p-invalid" : ""}
									   tooltip={errors.has(AnnotationStrikeDialogFields.CREATOR) ? Translate.value(
										   errors.get(AnnotationStrikeDialogFields.CREATOR)!, {ns: "message"}
									   ) : ""}
									   value={FunctionHelper.applyValue(
										   annotationParameter.creator, MarkupAnnotation.getCreatorDefault()
									   )}
									   onInput={(e) => this.onChange(
										   AnnotationStrikeDialogFields.CREATOR, e.currentTarget.value
									   )}/>
							{this.renderFieldError(errors.has(AnnotationStrikeDialogFields.CREATOR) ?
								Translate.value(
									errors.get(AnnotationStrikeDialogFields.CREATOR)!, {ns: "message"}
								) : ""
							)}
						</div>
					</div>
					<div className="p-col-12 p-grid"
						 hidden={!this.state.fields?.contains(AnnotationStrikeDialogFields.SUBJECT)}>
						<label className="p-col-12 p-sm-4" htmlFor="control-subject">
							<Translate {...this.props} data="annotation.properties.subject.label" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-8 p-grid">
							<InputText id="control-subject"
									   value={FunctionHelper.applyValue(
										   annotationParameter.subject, MarkupAnnotation.getSubjectDefault()
									   )}
									   onInput={(e) => this.onChange(
										   AnnotationStrikeDialogFields.SUBJECT, e.currentTarget.value
									   )}/>
						</div>
					</div>
					<div className="p-col-12 p-grid"
						 hidden={!this.state.fields?.contains(AnnotationStrikeDialogFields.CONTENTS)}>
						<label className="p-col-12 p-sm-4" htmlFor="control-contents">
							<Translate {...this.props} data="annotation.properties.content.label" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-8 p-grid">
							<InputTextarea rows={3} id="control-contents"
										   value={FunctionHelper.applyValue(
											   annotationParameter.contents, MarkupAnnotation.getContentsDefault()
										   )}
										   onInput={(e) => this.onChange(
											   AnnotationStrikeDialogFields.CONTENTS, e.currentTarget.value
										   )}/>
						</div>
					</div>
					<div className="p-col-12 p-grid"
						 hidden={!this.state.fields?.contains(AnnotationStrikeDialogFields.INTENTS)}>
						<label className="p-col-12 p-sm-4" htmlFor="control-intents">
							<Translate {...this.props} data="annotation.properties.intents" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-8 p-grid">
							<InputText id="control-intents"
									   value={FunctionHelper.applyValue(
										   annotationParameter.intents, MarkupAnnotation.getIntentsDefault()
									   )}
									   onInput={(e) => this.onChange(
										   AnnotationStrikeDialogFields.INTENTS, e.currentTarget.value
									   )}/>
						</div>
					</div>
				</fieldset>

				<fieldset className="p-col-12 p-grid">
					<div className="p-col-12 p-grid"
						 hidden={!this.state.fields?.contains(AnnotationStrikeDialogFields.COLOR)}>
						<label className="p-col-12 p-sm-4" htmlFor="control-color">
							<Translate {...this.props} data="annotation.appearance.color.label" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-8">
							<div className={"p-inputgroup" + (
								errors.has(AnnotationStrikeDialogFields.COLOR) ? " p-invalid" : ""
							)}>
								<InputText
									className={errors.has(AnnotationStrikeDialogFields.COLOR) ? "p-invalid" : ""}
									tooltip={errors.has(AnnotationStrikeDialogFields.COLOR) ? Translate.value(
										errors.get(AnnotationStrikeDialogFields.COLOR)!, {ns: "message"}
									) : ""}
									value={FunctionHelper.applyValue(
										annotationParameter.color, MarkupAnnotation.getColorDefault()
									)}
									onInput={(e) => this.onChange(
										AnnotationStrikeDialogFields.COLOR, e.currentTarget.value
									)}/>
								<ColorPicker inputId="control-color" className="p-inputgroup-addon" format="hex"
											 defaultColor={MarkupAnnotation.getColorDefault().replace(
												 "#", ""
											 )}
											 value={FunctionHelper.applyValue(
												 annotationParameter.color, MarkupAnnotation.getColorDefault()
											 ).replace("#", "")}
											 onChange={(e) => this.onChange(
												 AnnotationStrikeDialogFields.COLOR, "#" + e.value
											 )}
											 appendTo={document.getElementById("root")}/>
							</div>
							{this.renderFieldError(errors.has(AnnotationStrikeDialogFields.COLOR) ?
								Translate.value(
									errors.get(AnnotationStrikeDialogFields.COLOR)!, {ns: "message"}
								) : ""
							)}
						</div>
					</div>
					<div className="p-col-12 p-grid"
						 hidden={!this.state.fields?.contains(AnnotationStrikeDialogFields.OPACITY)}>
						<label className="p-col-12 p-sm-4" htmlFor="control-opacity">
							<Translate {...this.props} data="annotation.appearance.opacity" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-8">
							<InputNumber inputId="control-opacity" step={1} suffix=" %" showButtons={true}
										 useGrouping={false}
										 value={FunctionHelper.applyValue(
											 annotationParameter.opacity, MarkupAnnotation.getOpacityDefault()
										 )}
										 min={MarkupAnnotation.getOpacityMin()} max={MarkupAnnotation.getOpacityMax()}
										 onChange={(e) => this.onChange(
											 AnnotationStrikeDialogFields.OPACITY, e.value
										 )}/>
						</div>
					</div>
				</fieldset>
			</>
		);
	}

	protected onChange(field: AnnotationStrikeDialogFields, value: any): void {
		let parameter: ToolboxAnnotationAnnotation = this.state.parameter!.data;
		let annotationParameter: MarkupAnnotation = parameter.edit!.markup!;
		let errors: Map<AnnotationStrikeDialogFields, string> = this.state.fieldErrors;

		switch (field) {
			case AnnotationStrikeDialogFields.CREATOR:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					errors.set(field, "errors.fields.set");
				} else if (errors.has(field)) {
					errors.delete(field);
				}

				annotationParameter.creator = value;
				break;
			case AnnotationStrikeDialogFields.SUBJECT:
				annotationParameter.subject = value;
				break;
			case AnnotationStrikeDialogFields.CONTENTS:
				annotationParameter.contents = value;
				break;
			case AnnotationStrikeDialogFields.INTENTS:
				annotationParameter.intents = value;
				break;
			case AnnotationStrikeDialogFields.COLOR:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					errors.set(field, "errors.fields.set");
				} else if (!validator.isHexColor(value)) {
					errors.set(field, "errors.fields.color");
				} else if (errors.has(field)) {
					errors.delete(field);
				}

				annotationParameter.color = value;
				break;
			case AnnotationStrikeDialogFields.OPACITY:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = MarkupAnnotation.getOpacityDefault();
				}

				annotationParameter.opacity = value;
				break;
		}

		this.setState({
			parameter: this.state.parameter
		});
	}

	protected renderHeader(): ReactNode {
		return (
			<div>
				<h1>
					<Icon icon={"icon-edit"}/>
					<Translate {...this.props} data="annotation.edit" namespace="dialog"/>
				</h1>
				<Button icon="icon icon-close" onClick={() => this.hide()}/>
				{this.renderHelpButton()}
			</div>
		);
	}

	protected isEditPasswordNeeded(portalDocument: PortalDocument): boolean {
		return super.isEditPasswordNeeded(portalDocument) && !FunctionHelper.applyValue(
			portalDocument.metadata?.security?.canModifyAnnotations, Security.getCanModifyAnnotationsDefault()
		);
	}

	protected async submit(): Promise<void> {
		await super.submit();

		let toolboxOperation: BaseToolbox = BaseToolbox.fromJson({
			annotation: this.state.parameter!.data.toJson()
		});

		let operationData: Array<BaseToolbox> = [toolboxOperation];
		let webServiceType: WebServiceType = WebServiceTypes.TOOLBOX;
		let documentManager: PortalDocumentManager = this.props.context.documentManager;
		let selection: Array<PortalDocument> = documentManager.getSelection();

		for (let portalDocument of selection) {
			try {
				await this.props.context.session.executeRequest({
					webServiceType: webServiceType,
					document: portalDocument,
					operation: operationData,
					settings: Settings.fromJson({
						pdfa: {
							pdfaEditProfile: this.props.pdfaEditProfile
						},
						signature: {
							signatureEditProfile: this.props.signatureEditProfile
						},
						compress: {
							compressProfile: this.props.compressProfile,
							compressObjects: {
								objectStreamSize: this.props.objectStreamSize
							}
						}
					} as SettingsInterface)
				});
			} catch (exception: any) {
				if (await this.onError(exception)) {
					await this.submit();
				}
				return;
			}

			let message: Message = new Message("success.toolbox.annotation.edit.strike", MessageType.SUCCESS);
			message.context = {filename: '"' + portalDocument.getFileName() + '" '};
			this.props.context.getPage().addMessage(message);
			this.hide();
		}
	}

	protected getDialogType(): PortalDialogType {
		return PortalDialogType.ANNOTATION_STRIKE_EDIT;
	}

	protected getDefaultParameter(): Parameter<ToolboxAnnotationAnnotation> {
		return DialogDefault.getParameter(
			this.getDialogType(), this.props.context.documentManager.getSelection()
		);
	}
}

const AnnotationStrikeDialog = React.memo(AnnotationStrikeEditDialogDefinition,
	(
		prevProps: Readonly<PortalDialogProperties<ToolboxAnnotationAnnotation>>,
		nextProps: Readonly<PortalDialogProperties<ToolboxAnnotationAnnotation>>
	) => {
		if (!nextProps.visible && !prevProps.visible) {
			return true
		}

		return false;
	}
);

export default AnnotationStrikeDialog;